import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as rangeConstants from './constants';

import Bars from './bars';
import Line from './line';
import TextInputs from './text-inputs';
import Handles from './handles';
import Numbers from './numbers';

import * as S from './styles';

export default class Range extends Component {
  constructor(props) {
    super(props);

    const realMinMax = Range.getRealMinMax(props.values.values);
    const positionData = Range.calculatePositionData(
      props.values,
      props.selected
    );

    this.state = {
      positionData,
      realMinMax,
      selected: props.selected,
      dragging: false,
    };

    this.visibleNumbersAdjusted = null;
  }

  /**
   * We can't use props.values.max_values/min_values since they refer to
   * min max for actual products, not what the range contains.
   * @param values
   */
  static getRealMinMax(values) {
    const maxIndex = values.length - 1;

    return {
      from: values[0].from,
      to: values[maxIndex].to,
    };
  }

  static getSectionFill(section, value) {
    const range = section.to - section.from;
    const valueInRange = value - section.from;

    return valueInRange / range;
  }

  static getSectionValue(section, fill) {
    const range = section.to - section.from;
    return parseFloat((section.from + range * fill).toFixed(4));
  }

  static getSectionBounds(sectionCount) {
    return {
      [rangeConstants.KEY_LEFT]: {
        section: 0,
        fill: 0,
        position: 0,
      },
      [rangeConstants.KEY_RIGHT]: {
        section: sectionCount - 1,
        fill: 1,
        position: 1,
      },
    };
  }

  getSectionFillData = index => {
    // | = section, [---] = selection
    const { from, to } = this.state.positionData;
    // whole section inside selection. ie [--|--|--]
    if (index > from.section && index < to.section) {
      return { from: true, to: true, fill: 1 };
    }
    // section touches from right. ie |-[-|----]
    if (index === from.section && index !== to.section) {
      return { from: from.fill === 0, to: true, fill: 1 - from.fill };
    }

    // section touches from left. ie [----|-]-|
    if (index !== from.section && index === to.section) {
      return { from: true, to: to.fill === 1, fill: to.fill };
    }
    // whole selection inside section. . ie |-[-]-|
    if (index === from.section && index === to.section) {
      return { from: false, to: false, fill: to.fill - from.fill };
    }
    // section outside selection. ie [----]-|--|
    return { from: false, to: false, fill: 0 };
  };

  getSliderWidth = () => {
    const sliderNode = this.slider;
    return sliderNode.offsetWidth;
  };

  static findSectionIndexForValue(sections, value) {
    return sections.findIndex(section => {
      if (section.from <= value && section.to >= value) {
        return true;
      }
      return false;
    });
  }

  startDragging = callback => {
    this.setState({ dragging: true }, callback);
  };

  stopDragging = () => {
    this.setState({ dragging: false }, () => {
      this.commitChanges(this.state.selected);
    });
  };

  updateValueFromPosition = (key, positionPercentage) => {
    const sectionCount = this.props.values.values.length;
    const rawValueMapping = positionPercentage / (100 / sectionCount);

    let index = Math.floor(rawValueMapping);
    let fill = parseFloat((rawValueMapping % 1).toFixed(4));
    // make sure inside outer bounds
    const bounds = Range.getSectionBounds(sectionCount);

    if (index < bounds.from.section) {
      index = bounds.from.section;
      fill = bounds.from.fill;
    } else if (index > bounds.to.section) {
      index = bounds.to.section;
      fill = bounds.to.fill;
    }

    const newSelected = Object.assign({}, this.props.selected, {
      [key]: Range.getSectionValue(this.props.values.values[index], fill),
    });

    return newSelected;
  };

  adjustValueForPropertyType = value => {
    let step = null;
    const { type } = this.props;
    switch (type) {
      case rangeConstants.TYPE_INTEGER:
      case rangeConstants.TYPE_DATE_YEAR:
        step = 1;
        break;
      case rangeConstants.TYPE_DECIMAL:
      default:
        step = 10;
    }
    return Math.round(value * step) / step;
  };

  moveClosestHandle = (value, index) => {
    const { from, to } = this.state.positionData;
    const fromDifference = Math.abs(from.section + from.fill - index);
    const toDifference = Math.abs(to.section + to.fill - index);
    const newSelected = {};
    if (fromDifference < toDifference) {
      newSelected.from = value;
    } else {
      newSelected.to = value;
    }

    return newSelected;
  };

  static calculatePositionData(values, selected) {
    const sectionCount = values.values.length;
    if (!selected) {
      return Range.getSectionBounds(sectionCount);
    }

    return [rangeConstants.KEY_LEFT, rangeConstants.KEY_RIGHT].reduce(
      (positionData, key) => {
        const selectValue = selected[key];
        // get section value is in.
        const sectionData = {};

        const section = Range.findSectionIndexForValue(
          values.values,
          selectValue
        );
        if (section === -1) {
          // section not found which means selection is out of bound
          // (e.g. "I want 1tb RAM" is a valid filter even though 1tb RAM does not (yet) exist)
          sectionData.section =
            key === rangeConstants.KEY_LEFT ? 0 : sectionCount - 1;
          sectionData.fill = key === rangeConstants.KEY_LEFT ? 0 : 1;
          sectionData.position = key === rangeConstants.KEY_LEFT ? 0 : 1;
        } else {
          sectionData.section = section;
          sectionData.fill = Range.getSectionFill(
            values.values[section],
            selectValue
          );
          sectionData.position = (section + sectionData.fill) / sectionCount;
        }
        return Object.assign({}, positionData, { [key]: sectionData });
      },
      {}
    );
  }

  normalizeSelected = (selected, data) => {
    const { realMinMax } = this.state;
    let newSelected = Object.assign({}, realMinMax, selected, data);
    newSelected.from = this.adjustValueForPropertyType(newSelected.from);
    newSelected.to = this.adjustValueForPropertyType(newSelected.to);

    if (newSelected.from > newSelected.to) {
      const tempValue = newSelected.from;
      newSelected.from = newSelected.to;
      newSelected.to = tempValue;
    }

    if (
      newSelected.from === realMinMax.from &&
      newSelected.to === realMinMax.to
    ) {
      newSelected = null;
    }

    return newSelected;
  };

  onChange = event => {
    const selected = this.updateValueFromPosition(event.id, event.position);
    this.update(selected);
  };

  onTextInputChange = event => {
    const selected = { [event.id]: event.value };
    this.commitChanges(selected);
  };

  onBarClick = event => {
    this.commitChanges(event);
  };

  onNumberClick = event => {
    const selected = this.moveClosestHandle(event.value, event.index);
    this.commitChanges(selected);
  };

  commitChanges = selected => {
    this.props.onChange(this.update(selected));
  };

  update = selected => {
    const newSelected = this.normalizeSelected(this.state.selected, selected);

    const positionData = Range.calculatePositionData(
      this.props.values,
      newSelected
    );

    this.setState({
      positionData,
      selected: newSelected,
    });

    return newSelected;
  };

  componentDidUpdate(prevProps) {
    if (!this.props.selected && prevProps.selected !== this.props.selected) {
      // eslint-disable-next-line
      this.setState({
        positionData: Range.calculatePositionData(
          this.props.values,
          this.props.selected
        ),
        selected: this.props.selected,
      });
    }
  }

  render() {
    const { showBars, values, layout } = this.props;
    const { from, to } = this.state.positionData;
    const { selected, realMinMax, dragging } = this.state;

    const isSelected = {
      from: selected ? selected.from > realMinMax.from : false,
      to: selected ? selected.to < realMinMax.to : false,
    };

    return (
      <S.Layout
        layout={layout}
        ref={c => {
          this.range = c;
        }}
      >
        <TextInputs
          keys={[rangeConstants.KEY_LEFT, rangeConstants.KEY_RIGHT]}
          selected={this.state.selected}
          onChange={this.onTextInputChange}
          minMax={realMinMax}
          layout={layout}
        />
        <S.SliderWrapper
          ref={c => {
            this.slider = c;
          }}
          showBars={showBars}
        >
          {showBars && (
            <Bars
              values={values}
              onChange={this.onBarClick}
              getSectionFillData={this.getSectionFillData}
              layout={layout}
            />
          )}
          <Line
            invalid={!this.props.isValid}
            from={from.position}
            to={to.position}
            layout={layout}
          />
          <Handles
            keys={[rangeConstants.KEY_LEFT, rangeConstants.KEY_RIGHT]}
            selected={isSelected}
            invalid={!this.props.isValid}
            positionData={this.state.positionData}
            updateSlider={this.onChange}
            startDragging={this.startDragging}
            stopDragging={this.stopDragging}
            getSliderWidth={this.getSliderWidth}
            dragging={dragging}
          />
          <Numbers
            sections={values.values}
            onChange={this.onNumberClick}
            getSectionFillData={this.getSectionFillData}
            layout={layout}
          />
        </S.SliderWrapper>
      </S.Layout>
    );
  }
}

Range.propTypes = {
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  values: PropTypes.shape({
    max_matching_products: PropTypes.number,
    max_values: PropTypes.number,
    min_values: PropTypes.number,
    values: PropTypes.arrayOf(
      PropTypes.shape([
        PropTypes.shape({
          from: PropTypes.number,
          to: PropTypes.number,
          products: PropTypes.number,
        }),
      ])
    ),
  }).isRequired,
  type: PropTypes.string.isRequired,
  showBars: PropTypes.bool,
  isValid: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      from: PropTypes.number,
      to: PropTypes.number,
    }),
  ]),
};

Range.defaultProps = {
  layout: 'vertical',
  showBars: true,
  isValid: true,
  selected: undefined,
};
